import { LabReportTemplate } from './types';
import { CURRENT_SCHEMA_VERSION, DocumentSection } from '../../core/ast/document.types';
import { createDefaultPageSettings, createDefaultMetadata } from '../../core/ast/factory';
import { labReportCoverTemplate } from '../cover/labReportCover';
import { getDefaultCoverValues } from '../cover/templateUtils';

export const halfWaveRectifierLabReportTemplate: LabReportTemplate = {
  id: 'tpl-lab-half-wave-rectifier',
  name: 'Half-Wave Diode Rectifier Circuit (CSE-251 Expt 02)',
  category: 'electronics',
  description: 'Complete lab report blueprint for rectifier circuits with ripple voltage calculations, filter capacitance equations, pre-lab derivations, comparison tables, and waveform analysis.',
  thumbnailBadge: 'Rectifier Circuit',
  version: 2,
  sectionsBlueprint: [
    { title: 'Cover Page', type: 'cover', defaultContent: '' },
    { title: 'Title & Objectives', type: 'title', defaultContent: 'Title: Half-wave Diode Rectifier Circuit\nObjectives:\n1. To understand the working principle of a half-wave rectifier.\n2. To construct a half-wave rectifier circuit using a diode and resistor.\n3. To observe and analyze input AC and output DC waveforms using an oscilloscope.' },
    { title: 'Equipments and Components Needed', type: 'custom', defaultContent: '1. Signal Generator\n2. Digital Multimeter\n3. Diode (1 pc)\n4. Resistor 1 kΩ (1 pc)\n5. Capacitor 10 µF (1 pc)\n6. Breadboard\n7. Connecting wires' },
    { title: 'Theory', type: 'theory', defaultContent: 'A rectifier circuit converts an AC voltage with zero average into a unidirectional voltage with a non-zero average.' },
    { title: 'Circuit Diagram', type: 'custom', defaultContent: 'Figure-1: Set up for a half-wave diode rectifier circuit.' },
    { title: 'Pre-Lab Report Questions & Calculations', type: 'custom', defaultContent: 'Theoretical filter capacitance, conduction angle, and peak/average diode current calculations.' },
    { title: 'Experimental Data & Comparison Tables', type: 'data', defaultContent: 'Experimental measurements and comparison tables for voltage, time, and ripple discrepancy.' },
    { title: 'Post-Lab Questions & Solutions', type: 'custom', defaultContent: 'Post-lab waveform evaluations and PSpice circuit simulations.' },
    { title: 'Discussion', type: 'discussion', defaultContent: 'The experiment successfully studied how a half wave diode rectifier works, focusing on its basic principles, circuit construction, and filtering dynamics.' },
  ],
  compileToDocument: (customTitle?: string, pageSettingsOverrides?) => {
    const now = new Date().toISOString();

    const coverVals = getDefaultCoverValues({
      documentTypeLabel: 'Lab Report',
      title: 'Lab Report',
      experimentNumber: '02',
      experimentName: 'Half-wave Diode Rectifier Circuit',
      groupNumber: '',
      courseCode: 'CSE251',
      courseTitle: 'Electronic Circuits',
      sectionOrGroup: '',
      semester: '',
      studentName: '',
      studentId: '',
      facultyName: '',
      facultyDesignation: '',
      facultyDepartment: '',
      dateOfPerformance: '',
      submissionDate: '',
      submissionDisplayMode: 'both',
    });
    const coverDoc = labReportCoverTemplate.compileToDocument(coverVals, pageSettingsOverrides);
    const coverSection = coverDoc.sections[0];

    const sections: DocumentSection[] = [
      coverSection,
      // Section 1: Title, Objectives & Equipments
      {
        id: `sec-rect-title-${Date.now().toString(36)}`,
        title: 'Title, Objectives & Apparatus',
        type: 'title',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-r-t1-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Title: Half-wave Diode Rectifier Circuit\n\nObjectives:\nThe primary objectives of this experiment are:\n1. To understand the working principle of a half-wave rectifier.\n2. To construct a half-wave rectifier circuit using a diode and resistor.\n3. To observe and analyze the input alternating current (AC) and output direct current (DC) waveforms using an oscilloscope.\n4. To understand how a diode rectifies an AC signal by allowing current flow in only one direction.\n5. To analyze how a diode can be used to convert AC into DC.\n6. To determine the relationship between the peak input voltage and the peak output voltage.\n\nEquipments and Components Needed:\n1. Signal Generator\n2. Digital multimeter\n3. Diode (1 pc)\n4. Resistor 1 kΩ (1 pc)\n5. Capacitor 10 µF (1 pc)\n6. Breadboard\n7. Connecting wires',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.45 },
            },
          },
        ],
      },
      // Section 2: Theory & Circuit Diagram
      {
        id: `sec-rect-th-${Date.now().toString(36)}`,
        title: 'Theory & Circuit Diagram',
        type: 'theory',
        enabled: true,
        collapsed: false,
        blocks: [
          {
            id: `blk-r-th-body-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Theory:\nA rectifier circuit converts an AC voltage with zero average into a unidirectional voltage with a non-zero average. The rectifier circuit can rectify both positive and negative half-cycles (full-wave rectifier) or only the positive half-cycle (half-wave rectifier) of a sine wave. A capacitor connected across the load resistor acts as a filter and reduces the ripple of the output voltage. The time constant of the RC network should be much larger than the period of the AC source voltage for effective filtering.\n\nCircuit Diagram:\nFigure-1: Set up for a half-wave diode rectifier circuit.',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.45 },
            },
          },
        ],
      },
      // Section 3: Pre-Lab Calculations & Mathematical Formulations
      {
        id: `sec-rect-prelab-${Date.now().toString(36)}`,
        title: 'Pre-Lab Report Calculations',
        type: 'custom',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-r-pl-q1-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Pre-Lab Report Question:\n1. Consider the half-wave rectifier circuit fed by a 1.0 kHz sinusoid with peak value Vp = 5V. Determine the required parallel capacitor for peak-to-peak ripple Vr = 0.5V, and calculate conduction angle ωΔt, conduction time Δt, average diode current iDavg, and peak current iDmax.',
              formatting: { bold: true, fontSizePt: 10.5, color: '#111827', lineHeightMultiplier: 1.4 },
            },
          },
          {
            id: `blk-r-pl-f1-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: 'C = \\frac{V_p}{f R V_r} = \\frac{5\\text{ V}}{1000\\text{ Hz} \\times 1000\\ \\Omega \\times 0.5\\text{ V}} = 10 \\times 10^{-6}\\text{ F} = 10\\ \\mu\\text{F}',
              displayMode: true,
              equationNumber: '(Eq. 1)',
              caption: 'Filter Capacitor Calculation',
            },
          },
          {
            id: `blk-r-pl-f2-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: '\\omega \\Delta t = \\cos^{-1}\\left( \\frac{V_p - V_r}{V_p} \\right) = \\cos^{-1}\\left( \\frac{5 - 0.5}{5} \\right) = 0.451\\text{ rad}',
              displayMode: true,
              equationNumber: '(Eq. 2)',
              caption: 'Diode Conduction Angle',
            },
          },
          {
            id: `blk-r-pl-f3-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: '\\Delta t = \\frac{\\omega \\Delta t}{2\\pi f} = \\frac{0.451}{2\\pi \\times 1000} = 0.072\\text{ ms}',
              displayMode: true,
              equationNumber: '(Eq. 3)',
              caption: 'Diode Conduction Time',
            },
          },
          {
            id: `blk-r-pl-f4-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: 'i_{D\\text{avg}} = \\frac{V_p - \\frac{V_r}{2}}{R}\\left(1 + \\pi \\sqrt{\\frac{2 V_p}{V_r}}\\right) = \\frac{5 - 0.25}{1000}\\left(1 + \\pi \\sqrt{\\frac{10}{0.5}}\\right) = 71.45\\text{ mA}',
              displayMode: true,
              equationNumber: '(Eq. 4)',
              caption: 'Average Diode Current',
            },
          },
          {
            id: `blk-r-pl-f5-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: 'i_{D\\text{max}} = \\frac{V_p - \\frac{V_r}{2}}{R}\\left(1 + 2\\pi \\sqrt{\\frac{2 V_p}{V_r}}\\right) = \\frac{5 - 0.25}{1000}\\left(1 + 2\\pi \\sqrt{\\frac{10}{0.5}}\\right) = 138.22\\text{ mA}',
              displayMode: true,
              equationNumber: '(Eq. 5)',
              caption: 'Maximum Peak Diode Current',
            },
          },
        ],
      },
      // Section 4: Experimental Data & Comparison Tables
      {
        id: `sec-rect-data-${Date.now().toString(36)}`,
        title: 'Experimental Data & Comparison Tables',
        type: 'data',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-r-dt-h-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Experimental Data:\n• Load Resistance R = 0.987 kΩ\n• Peak Voltage ΔVp = 0.6767 V\n• Conduction Time Δt = 0.2 ms\n• Ripple Voltage Vr = 0.6968 V\n• Average Output Voltage Vo = 12.87 V\n\nComparison Table: Diode Built-in Voltage',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.45 },
            },
          },
          {
            id: `blk-r-tbl1-${Date.now().toString(36)}`,
            type: 'table',
            createdAt: now,
            data: {
              rows: 2,
              cols: 3,
              hasHeaderRow: true,
              hasHeaderCol: false,
              borderStyle: 'academic',
              caption: 'Comparison 1: Measured Built-in Voltage vs Theoretical',
              cells: [
                [
                  { id: 'c00', content: 'Measured Value (Expt 1)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c01', content: 'Built-in Voltage (Silicon)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c02', content: 'Discrepancy (%)', isHeader: true, bold: true, alignment: 'center' },
                ],
                [
                  { id: 'c10', content: '0.6767 V', alignment: 'center' },
                  { id: 'c11', content: '0.70 V', alignment: 'center' },
                  { id: 'c12', content: '3.3%', alignment: 'center' },
                ],
              ],
            },
          },
          {
            id: `blk-r-sp-t2-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: { text: '\nComparison Table: Conduction Time (Δt)', formatting: { bold: true, fontSizePt: 11 } },
          },
          {
            id: `blk-r-tbl2-${Date.now().toString(36)}`,
            type: 'table',
            createdAt: now,
            data: {
              rows: 2,
              cols: 3,
              hasHeaderRow: true,
              hasHeaderCol: false,
              borderStyle: 'academic',
              caption: 'Comparison 2: Conduction Time Δt',
              cells: [
                [
                  { id: 'c00', content: 'Measured Value Δt', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c01', content: 'Calculated Pre-Lab Δt', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c02', content: 'Discrepancy (%)', isHeader: true, bold: true, alignment: 'center' },
                ],
                [
                  { id: 'c10', content: '0.20 ms', alignment: 'center' },
                  { id: 'c11', content: '0.072 ms', alignment: 'center' },
                  { id: 'c12', content: '64.0%', alignment: 'center' },
                ],
              ],
            },
          },
          {
            id: `blk-r-sp-t3-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: { text: '\nComparison Table: Diode Current Performance', formatting: { bold: true, fontSizePt: 11 } },
          },
          {
            id: `blk-r-tbl3-${Date.now().toString(36)}`,
            type: 'table',
            createdAt: now,
            data: {
              rows: 3,
              cols: 4,
              hasHeaderRow: true,
              hasHeaderCol: false,
              borderStyle: 'academic',
              caption: 'Comparison 3: Average & Peak Diode Currents',
              cells: [
                [
                  { id: 'c00', content: 'Parameter', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c01', content: 'Measured Value', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c02', content: 'Calculated Pre-Lab Value', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c03', content: 'Discrepancy (%)', isHeader: true, bold: true, alignment: 'center' },
                ],
                [
                  { id: 'c10', content: 'iDavg', bold: true, alignment: 'center' },
                  { id: 'c11', content: '70.78 mA', alignment: 'center' },
                  { id: 'c12', content: '71.45 mA', alignment: 'center' },
                  { id: 'c13', content: '0.938%', alignment: 'center' },
                ],
                [
                  { id: 'c20', content: 'iDmax', bold: true, alignment: 'center' },
                  { id: 'c21', content: '128.34 mA', alignment: 'center' },
                  { id: 'c22', content: '138.22 mA', alignment: 'center' },
                  { id: 'c23', content: '7.148%', alignment: 'center' },
                ],
              ],
            },
          },
        ],
      },
      // Section 5: Discussion & Conclusion
      {
        id: `sec-rect-disc-${Date.now().toString(36)}`,
        title: 'Discussion',
        type: 'discussion',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-r-disc-body-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Discussion:\n\nThe experiment successfully demonstrated the fundamental behavior of a p-n junction diode in converting alternating current (AC) into unidirectional pulsating DC current. Adding a parallel capacitor across the load resistor effectively smoothed out the ripple voltage Vr.\n\nThe measured diode cut-in voltage ΔVp (0.6767 V) was very close to the theoretical value of 0.7 V with a minimal discrepancy of only 3.3%. PSPICE circuit simulation confirmed the AC sinusoidal input waveform conversion and transient charge/discharge cycles across the 10 µF filtering capacitor.\n\nDiscrepancies observed in conduction time Δt and peak ripple can be attributed to non-ideal component tolerances (±10% on electrolytic capacitors), internal generator source resistance, and scope probe impedance loading. Future work should investigate full-wave bridge topologies to reduce ripple factor and improve transformer utilization.',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.5 },
            },
          },
        ],
      },
    ];

    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      metadata: createDefaultMetadata(customTitle || 'CSE-251 Lab Report 02: Half-wave Rectifier', 'lab_report'),
      pageSettings: { ...createDefaultPageSettings(), ...pageSettingsOverrides },
      sections,
    };
  },
};
